<script setup lang="ts">
import { storeToRefs } from 'pinia';
import VehicleCardComponent from '@/components/VehicleCardComponent.vue';
import { ref } from 'vue';
import BottomBarComponent from '@/components/BottomBarComponent.vue';
import BackComponent from '@/components/BackComponent.vue';
import axios from 'axios';
import { CreateServicePlanDto, type CustomerDto, type RequestQuoteDto } from '@/types';
import { useCustomerStore } from '@/stores/customer';
import { useAgreementStore } from '@/stores/agreement';
import { useVehicleStore } from '@/stores/vehicle';
import { useConfigStore } from '@/stores/config';
import { useMessageStore } from '@/stores/messageStore';
import { useRouter } from 'vue-router';
import AgreementSummaryComponent from '@/components/AgreementSummaryComponent.vue';

const router = useRouter()
const { savedCustomer } = storeToRefs(useCustomerStore())
const { config } = storeToRefs(useConfigStore())
const { details, manualVehicle, isManualInput } = storeToRefs(useVehicleStore())
const { agreement, agreementType, selectedAddons, includedAddons, calculation, basePrice, addonsPrice } = storeToRefs(useAgreementStore())
const message = useMessageStore()

const submitting = ref(false)
const disclaimerDialog = ref(false)
const hasAcceptedTerms = ref(false)

const consentHtml = `SAMTYKKE: Ja tak, jeg vil gerne modtage info og materiale fra AFTALEUDBYDER via brev, telefon og elektronisk post (f.eks. e-mail, sms, mms) samt ringless voicemails med henblik på markedsføring af biler, tilbehør til biler, reparation/service af biler, events, konkurrencer og kundetilfredshedsundersøgelser. Jeg accepterer endvidere, at mine oplysninger behandles automatisk til brug for markedsføringsformål dog ikke videresalg.
<br><br>Jeg giver samtidigt tilladelse til, at AFTALEUDBYDER må videregive mine oplysninger til HOLCO Solution, med det formål at kontakte mig som ovenfor nævnt samt håndtere opkrævning af min aftale.
AFTALEUDBYDERs privatlivspolitik fremgår af udbyders hjemmeside.`

function onAcceptTerms() {
  hasAcceptedTerms.value = true
  disclaimerDialog.value = false

  if (isManualInput.value) {
    requestQuote()
  } else {
    onCreateServicePlan()
  }
}

async function createCustomer(customer: CustomerDto) {
  customer.status = "1" // Must be one to be active
  
  const { data } = await axios.post('proxy/customers', customer)

  return data.id
}

async function createServicePlan() {
  const addons = selectedAddons.value.concat(includedAddons.value)
  const agreementresponsible = agreement.value.agreementresponsible ?? undefined
  const servicePlanDto = new CreateServicePlanDto(agreement.value, addons, agreementresponsible)

  // Change brand, model and engine IDs if they are explicitly set in agreement type
  if (agreementType.value) {
    const { customBrandId, customModelId, customEngineId } = agreementType.value

    if (!!customBrandId && !!customModelId && !!customEngineId) {
      servicePlanDto.brandId = customBrandId
      servicePlanDto.modelId = customModelId
      servicePlanDto.engineId = customEngineId
    }
  }

  const { data } = await axios.post('proxy/serviceplan', servicePlanDto)

  console.log(data)
}

async function onCreateServicePlan() {
  if (!hasAcceptedTerms.value) {
    disclaimerDialog.value = true
    return
  }

  submitting.value = true

  try {
    if (!agreement.value.customerId) {
      if (!savedCustomer.value) {
        throw 'No customer defined'
      }
  
      const customerId = await createCustomer(savedCustomer.value)
  
      agreement.value.customerId = customerId
    }
  
    await createServicePlan()
    router.push('/agreement/finish')
  } catch (error) {
    message.addError('Der skete en fejl ved oprettelse af aftalen. Prøv venligst igen senere og kontakt os på +45 3116 9202 eller sam@holco.dk hvis problemet fortsætter.')
  } finally {
    submitting.value = false
  }
}

async function requestQuote() {
  if (!hasAcceptedTerms.value) {
    disclaimerDialog.value = true
    return
  }

  submitting.value = true

  try {
    if (!savedCustomer.value) {
      throw 'No customer defined'
    }

    const { name, email, mobile, address1, zipCode, city, status } = savedCustomer.value
    const { registrationYear, licensePlate, vinNumber, oldMileage, duration, yearlyMileage, serviceDate, agreementresponsible } = agreement.value
  
    const requestDto: RequestQuoteDto = {
      name: name as string,
      email: email as string,
      mobile: mobile as string,
      address1: address1 as string,
      zipCode: zipCode as string,
      city: city as string,
      status: status?.toString() ?? '',
      brand: details.value?.brand ?? manualVehicle.value?.brand?.name ?? 'Ukendt mærke',
      model: details.value?.model ?? manualVehicle.value?.model?.name ?? 'Ukendt model',
      engine: details.value?.engine ?? manualVehicle.value?.engine?.name ?? 'Ukendt motor',
      registrationYear,
      licensePlate: licensePlate ?? '',
      vinNumber: vinNumber ?? '',
      oldMileage,
      duration: duration as number,
      yearlyMileage: yearlyMileage as number,
      latestService: serviceDate ?? '',
      servicePlanPrice: calculation.value?.servicePlanPrice,
      servicePlanPriceWithAddons: basePrice.value + addonsPrice.value,
      addonNames: selectedAddons.value.map(addon => addon.name) ?? [],
      comments: `${config.value?.optionsLabel ?? 'Valg'}: ${agreementresponsible}`
    }

    const { data } = await axios.post('proxy/serviceplan/request', requestDto)

    router.push('/agreement/finish?manual=true')
  } catch (error) {
    console.error(error)
    message.addError('Der skete en fejl ved anmodning om aftalen. Prøv venligst igen senere og kontakt os på +45 3116 9202 eller sam@holco.dk hvis problemet fortsætter.')
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <AgreementSummaryComponent />
      </v-col>
      <v-col cols="12">
        <VehicleCardComponent />
      </v-col>
      <v-col cols="12">
        <v-card :title="savedCustomer?.name" append-icon="mdi-account-circle-outline">
          <v-card-text>
            <div class="d-flex flex-column" style="gap: .25rem;">
              <div class="d-flex align-center" style="gap: .5rem;">
                <v-icon>mdi-email-outline</v-icon>
                <span>{{ savedCustomer?.email }}</span>
              </div>
              <div class="d-flex align-center" style="gap: .5rem;">
                <v-icon>mdi-phone-outline</v-icon>
                <span>{{ savedCustomer?.mobile }}</span>
              </div>
              <div class="d-flex align-center" style="gap: .5rem;">
                <v-icon>mdi-map-marker-outline</v-icon>
                <span>{{ savedCustomer?.address1 }}, {{ savedCustomer?.zipCode }} {{ savedCustomer?.city }}</span>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>

  <v-dialog
    v-model="disclaimerDialog"
    max-width="800"
    scrollable
  >
    <v-card>
      <v-card-text>
        <p v-html="consentHtml" />
      </v-card-text>
      <v-card-text v-if="isManualInput">
        Jeg accepterer, at mine informationer er korrekte og at disse sendes til forhandleren. Jeg er indforstået med, at dette er en forespørgsel og ikke en gældende aftale. Forhandleren kan til enhver tid afslå aftalen.
      </v-card-text>
      <v-card-text v-else>
        Jeg accepterer, at mine informationer er korrekte og at disse sendes til forhandleren. Jeg er indforstået med, at dette er en gældende aftale. Forhandleren kan til enhver tid afslå aftalen, såfremt der er givet misinformationer eller kunden på anden vis ikke lever op til betingelserne for aftalen.
      </v-card-text>
      <v-card-actions>
        <v-btn @click="disclaimerDialog = false">
          Afvis
        </v-btn>
        <v-spacer />
        <v-btn class="bg-success" @click="onAcceptTerms">
          Jeg accepterer
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <BottomBarComponent>
    <BackComponent to="/agreement/customer" />
    <v-btn
      v-if="!isManualInput"
      class="ml-auto bg-success"
      :loading="submitting"
      @click="onCreateServicePlan"
    >
      <v-icon>mdi-check-circle-outline</v-icon>
      <span>Godkend aftale</span>
    </v-btn>
    <v-btn
      v-else
      class="ml-auto bg-success"
      :loading="submitting"
      @click="requestQuote"
    >
      <v-icon>mdi-send-outline</v-icon>
      <span>Anmod om aftale</span>
    </v-btn>
  </BottomBarComponent>
</template>